import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ArrowRightIcon, X } from 'lucide-react';
import {
  getAISurveyFeedback,
  createConversation,
  type AISurveyAnswerResponse,
} from '../services/backendService';
import { useLanguage } from '../lib/i18n/useLanguage';
import { useTranslate } from '../lib/i18n';
import { Survey, ApiSurveyQuestion } from '../services/surveyBackendService';
import { extractErrorCode } from '../utils/errorHandler';
import { useSubmitSurvey } from '../hooks/mutations/useSubmitSurvey';
import Keywords from './Keywords';
import Button from './common/Button';
import SurveyProgressIndicator from './SurveyProgressIndicator';
import ErrorToast from './ErrorToast';
import { TagPill } from './TagPill';

interface IntakeToolFormProps {
  survey: Survey;
  answers: Record<number, string | string[]>;
  onAnswersChange: (answers: Record<number, string | string[]>) => void;
  userProfileTags: Record<string, boolean>;
  onSurveySubmitted: (feedback: AISurveyAnswerResponse, conversationCreatedAt: Date) => void;
  surveyQuestions: ApiSurveyQuestion[];
  surveyId: string;
}

const IntakeToolForm = ({
  survey,
  answers,
  onAnswersChange,
  userProfileTags,
  onSurveySubmitted,
  surveyQuestions,
  surveyId,
}: IntakeToolFormProps) => {
  const [error, setError] = useState<string | null>(null);
  const [surveySaveError, setSurveySaveError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [currentStage, setCurrentStage] = useState(-1);
  const [startTime, setStartTime] = useState<number>(0);
  const [showDisabledMessage, setShowDisabledMessage] = useState(false);
  const progressTimersRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const { language } = useLanguage();
  const t = useTranslate();

  const { mutate: submitSurvey } = useSubmitSurvey();

  const hasAnyAnswers = (() => {
    const sections = [
      ...new Set(survey.questions.filter((q) => !q.isRequired).map((q) => q.section)),
    ];
    return sections.every((section) => {
      const questionsInSection = survey.questions
        .map((q, index) => ({ question: q, index }))
        .filter(({ question }) => question.section === section && !question.isRequired);

      return questionsInSection.some(({ index }) => {
        const answer = answers[index];
        if (answer === undefined || answer === null) return false;
        if (Array.isArray(answer)) return answer.length > 0;
        if (typeof answer === 'string') return answer.trim().length > 0;
        return false;
      });
    });
  })();

  const getProgressStages = useCallback(
    () => [
      { message: t('survey.progress.processingAnswers'), duration: 10000 },
      { message: t('survey.progress.processingAnswers'), duration: 10000 },
      { message: t('survey.progress.searchingDatabase'), duration: 10000 },
      { message: t('survey.progress.searchingDatabase'), duration: 10000 },
      { message: t('survey.progress.preparingReport'), duration: 10000 },
      { message: t('survey.progress.preparingReport'), duration: 10000 },
    ],
    [t]
  );

  useEffect(() => {
    return () => {
      progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    };
  }, []);

  useEffect(() => {
    if (hasAnyAnswers && showDisabledMessage) {
      setShowDisabledMessage(false);
    }
  }, [hasAnyAnswers, showDisabledMessage]);

  const startProgressSimulation = () => {
    progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    progressTimersRef.current = [];

    setStartTime(Date.now());
    setCurrentStage(0);

    const stages = getProgressStages();
    let cumulativeDelay = stages[0]?.duration || 0;
    stages.forEach((stage, index) => {
      if (index > 0) {
        const timer = setTimeout(() => setCurrentStage(index), cumulativeDelay);
        progressTimersRef.current.push(timer);
        cumulativeDelay += stage.duration;
      }
    });
  };

  const resetProgress = () => {
    progressTimersRef.current.forEach((timer) => clearTimeout(timer));
    progressTimersRef.current = [];
    setCurrentStage(-1);
    setStartTime(0);
  };

  const handleSubmit = async () => {
    if (submitting) return;

    try {
      setError(null);
      startProgressSimulation();

      const questions = survey.questions.map((q) => q.mainText ?? '');
      const answerValues: string[] = [];

      survey.questions.forEach((_, index) => {
        const answer = answers[index];
        if (answer) {
          const translatedAnswer = Array.isArray(answer)
            ? answer.map((key) => t(key)).join(', ')
            : t(answer);
          answerValues.push(translatedAnswer);
        } else {
          answerValues.push('');
        }
      });

      // Save to survey backend (non-blocking for AI feedback)
      submitSurvey(
        { surveyId, questions: surveyQuestions, answers },
        {
          onError: (saveError) => {
            console.error('Failed to save survey response:', saveError);
            const errorCode = extractErrorCode(saveError);
            const errorMessage = t(`common.errors.${errorCode}`, {
              defaultValue: t('survey.errors.server_error'),
            });
            setSurveySaveError(errorMessage);
          },
          onSuccess: () => setSurveySaveError(null),
        }
      );

      const conversationCreatedAt = new Date();
      const conversationResponse = await createConversation({
        language,
        type: 'USER',
      });

      setSubmitting(true);
      try {
        const feedbackResponse = await getAISurveyFeedback({
          conversationId: conversationResponse.id,
          questions,
          answers: answerValues,
          language,
        });

        resetProgress();
        setError(null);
        onSurveySubmitted(feedbackResponse, conversationCreatedAt);
      } catch (err) {
        console.error('Failed to get AI feedback:', err);
        const errorCode = extractErrorCode(err);
        const errorMessage = t(`common.errors.${errorCode}`, {
          defaultValue: t('common.errors.generic'),
        });
        setError(errorMessage);
        resetProgress();
      } finally {
        setSubmitting(false);
      }
    } catch (err) {
      console.error('Failed to create conversation:', err);
      const errorCode = extractErrorCode(err);
      const errorMessage = t(`common.errors.${errorCode}`, {
        defaultValue: t('common.errors.generic'),
      });
      setError(errorMessage);
      resetProgress();
      setSubmitting(false);
    }
  };

  const handleRetry = () => {
    setError(null);
    handleSubmit();
  };

  const handleDisabledButtonClick = () => {
    if (!hasAnyAnswers && !submitting) setShowDisabledMessage(true);
  };

  const handleDismissMessage = () => setShowDisabledMessage(false);

  return (
    <div className="min-h-full flex flex-col">
      <div className="flex-1 flex flex-col">
        <div className="flex-1" />
        <div className="max-w-lg mx-auto pt-4 pb-4 space-y-4">
          <div className="border rounded-md text-sm p-3 space-y-2 bg-muted">
            <p>{t('SurveyTool.title_1')}</p>
            <p>{t('SurveyTool.title_2')}</p>
          </div>

          {[...new Set(survey.questions.filter((q) => !q.isRequired).map((q) => q.section))].map(
            (section, i) => (
              <div key={i} className="bg-card border rounded-md p-3 space-y-4">
                <div className="flex items-center justify-between">
                  <div>{section?.toUpperCase()}</div>
                  {userProfileTags['underage'] && section === t('survey.victimProfile') && (
                    <TagPill text={t('survey.victimIsMinor')} />
                  )}
                  {userProfileTags['domestic'] && section === t('survey.crimeOverview') && (
                    <TagPill text={t('riku.domesticViolence')} />
                  )}
                </div>
                <Keywords
                  key={i}
                  onAnswersChange={onAnswersChange}
                  questions={survey.questions.filter((q) => q.section === section && !q.isRequired)}
                  originalSurvey={survey}
                  answers={answers}
                />
              </div>
            )
          )}
        </div>
      </div>

      <div className="bg-background border-t sticky bottom-0 -mx-4">
        <div className="py-4 max-w-lg mx-auto px-4 space-y-4 box-content">
          {submitting && (
            <SurveyProgressIndicator
              currentStage={currentStage}
              stages={getProgressStages()}
              startTime={startTime}
            />
          )}

          {showDisabledMessage && !submitting && (
            <div className="border rounded-md text-sm p-3 space-y-2 bg-muted relative">
              <button
                className="absolute top-1 right-1 p-1 hover:bg-muted-foreground/10 rounded"
                onClick={handleDismissMessage}
                aria-label={t('common.buttons.close')}
              >
                <X className="size-4" />
              </button>
              <div className="pr-8">
                <p>{t('SurveyTool.title_1')}</p>
                <p>{t('SurveyTool.title_2')}</p>
              </div>
            </div>
          )}

          {error && !submitting && (
            <div className="p-4 bg-destructive/10 border border-destructive rounded-md">
              <div className="text-destructive mb-2">{error}</div>
              <Button variant="secondary" onClick={handleRetry} disabled={submitting}>
                {t('common.buttons.retry')}
              </Button>
            </div>
          )}

          {!error && (
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Button
                  variant="primary"
                  onClick={handleSubmit}
                  disabled={!hasAnyAnswers || submitting}
                  className="flex items-center justify-center gap-2 h-full"
                >
                  {submitting ? t('survey.handling') : t('survey.send')}
                  <ArrowRightIcon className="size-5" />
                </Button>
                {(!hasAnyAnswers || submitting) && (
                  <div
                    role="button"
                    tabIndex={0}
                    className="absolute inset-0 cursor-pointer"
                    onClick={handleDisabledButtonClick}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleDisabledButtonClick();
                      }
                    }}
                    aria-label={t('survey.send')}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <ErrorToast
        error={surveySaveError}
        onClose={() => setSurveySaveError(null)}
        duration={5000}
      />
    </div>
  );
};

export default IntakeToolForm;
